/**
 * ProfileForm — presentational component for editing a HealthProfile.
 *
 * Receives all state via props; no network calls here.
 * Nine sections with <fieldset><legend> for accessibility.
 * Medical disclaimer shown above clinical sections (ADR-7).
 *
 * Accessibility (spec §Accessibility, ADR-3):
 *   - Every input has <label htmlFor> or aria-label.
 *   - aria-invalid + aria-describedby on fields with errors.
 *   - min-h-[44px] on interactive elements.
 *   - focus-visible:ring-2 ring-blue-400.
 *   - Errors announced via role="alert".
 *   - No dangerouslySetInnerHTML.
 *
 * Validation: client-side range check via validateHealthProfileRanges
 * from @autodieta/shared before submit.
 */

import { useState, useRef, useCallback, type FormEvent, type KeyboardEvent } from "react";
import type { HealthProfile, Allergen, Intolerance, MedicalCondition, DietType, ActivityLevel, Objective, WeekdayActivity, DayActivity, DayActivityType, ActivityIntensity } from "@autodieta/shared";
import { validateHealthProfileRanges, validateEnums } from "@autodieta/shared";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProfileFormProps {
  value: Partial<HealthProfile>;
  onChange(updated: Partial<HealthProfile>): void;
  onSubmit(value: Partial<HealthProfile>): void;
  saving: boolean;
  error?: string;
  successMessage?: string;
}

// ---------------------------------------------------------------------------
// Constants (typed enum options for selects / multi-selects)
// ---------------------------------------------------------------------------

const ACTIVITY_OPTIONS: { value: ActivityLevel; label: string }[] = [
  { value: "INACTIVA", label: "Inactivo/a (sin ejercicio)" },
  { value: "BAJA", label: "Baja (menos de 1 día/semana)" },
  { value: "LIGERA", label: "Ligera (1–3 días/semana)" },
  { value: "MEDIA", label: "Moderada (3–5 días/semana)" },
  { value: "ALTA", label: "Alta (6–7 días/semana)" },
  { value: "MUY_ALTA", label: "Muy alta (trabajo físico intenso)" },
  { value: "ALTO_GRADO", label: "Alto grado (atleta de competencia)" },
  { value: "EXTREMA", label: "Extrema" },
  { value: "FREAK", label: "Freak" },
  { value: "INSANE", label: "Insane" },
];

const OBJECTIVE_OPTIONS: { value: Objective; label: string }[] = [
  { value: "restriccion_severa", label: "Restricción severa" },
  { value: "bajada_peso", label: "Bajada de peso" },
  { value: "definicion", label: "Definición" },
  { value: "bajada_10", label: "Bajada 10%" },
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "aumento_10", label: "Aumento 10%" },
  { value: "aumento_peso", label: "Aumento de peso" },
  { value: "hipertrofia", label: "Hipertrofia" },
  { value: "carga_carbohidratos", label: "Carga de carbohidratos" },
];

const DIET_TYPE_OPTIONS: { value: DietType; label: string }[] = [
  { value: "omnivore", label: "Omnívoro" },
  { value: "vegetarian", label: "Vegetariano" },
  { value: "vegan", label: "Vegano" },
  { value: "pescatarian", label: "Pescetariano" },
  { value: "flexitarian", label: "Flexitariano" },
];

const ALLERGEN_OPTIONS: { value: Allergen; label: string }[] = [
  { value: "milk", label: "Leche" },
  { value: "egg", label: "Huevo" },
  { value: "peanut", label: "Maní" },
  { value: "tree_nuts", label: "Frutos secos" },
  { value: "soy", label: "Soja" },
  { value: "wheat_gluten", label: "Trigo / Gluten" },
  { value: "fish", label: "Pescado" },
  { value: "shellfish", label: "Mariscos" },
  { value: "sesame", label: "Sésamo" },
  { value: "sulphites", label: "Sulfitos" },
  { value: "celery", label: "Apio" },
  { value: "mustard", label: "Mostaza" },
  { value: "lupin", label: "Altramuz" },
  { value: "molluscs", label: "Moluscos" },
  { value: "other", label: "Otra" },
];

const INTOLERANCE_OPTIONS: { value: Intolerance; label: string }[] = [
  { value: "lactose", label: "Lactosa" },
  { value: "fructose_sorbitol", label: "Fructosa / Sorbitol" },
  { value: "fodmaps", label: "FODMAPs" },
  { value: "histamine", label: "Histamina" },
  { value: "caffeine", label: "Cafeína" },
  { value: "gluten_non_celiac", label: "Gluten (no celíaco)" },
];

const CONDITION_OPTIONS: { value: MedicalCondition; label: string }[] = [
  { value: "diabetes_t1", label: "Diabetes tipo 1" },
  { value: "diabetes_t2", label: "Diabetes tipo 2" },
  { value: "prediabetes", label: "Prediabetes" },
  { value: "hypertension", label: "Hipertensión" },
  { value: "dyslipidemia", label: "Dislipidemia" },
  { value: "ckd", label: "Enfermedad renal crónica" },
  { value: "celiac", label: "Celiaquía" },
  { value: "ibs_ibd", label: "SII / EII" },
  { value: "hypo_hyperthyroidism", label: "Tiroides (hipo/hiper)" },
  { value: "pcos_insulin_resistance", label: "SOP / Resistencia a la insulina" },
  { value: "nafld", label: "Hígado graso (NAFLD)" },
  { value: "gout", label: "Gota" },
  { value: "gerd_gastritis", label: "Reflujo / Gastritis" },
  { value: "iron_deficiency_anemia", label: "Anemia ferropénica" },
  { value: "pregnancy_lactation", label: "Embarazo / Lactancia" },
  { value: "other", label: "Otra" },
];

// ---------------------------------------------------------------------------
// Small reusable sub-components
// ---------------------------------------------------------------------------

interface FieldErrorProps {
  id: string;
  message: string;
}

function FieldError({ id, message }: FieldErrorProps): JSX.Element {
  return (
    <p id={id} role="alert" className="mt-1 text-xs text-red-400">
      {message}
    </p>
  );
}

interface ChipsInputProps {
  id: string;
  label: string;
  value: string[];
  onChange(updated: string[]): void;
  placeholder?: string;
}

function ChipsInput({ id, label, value, onChange, placeholder }: ChipsInputProps): JSX.Element {
  const [inputVal, setInputVal] = useState("");

  const addChip = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
      }
      setInputVal("");
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addChip(inputVal);
      } else if (e.key === "Backspace" && inputVal === "" && value.length > 0) {
        onChange(value.slice(0, -1));
      }
    },
    [inputVal, value, onChange, addChip],
  );

  const removeChip = useCallback(
    (chip: string) => {
      onChange(value.filter((v) => v !== chip));
    },
    [value, onChange],
  );

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-zinc-300">
        {label}
      </label>
      <div className="flex flex-wrap gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1">
        {value.map((chip) => (
          <span
            key={chip}
            className="flex items-center gap-1 rounded bg-blue-600/30 px-2 py-0.5 text-xs text-blue-200"
          >
            {chip}
            <button
              type="button"
              aria-label={`Eliminar ${chip}`}
              className="ml-1 text-blue-400 hover:text-red-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400"
              onClick={() => removeChip(chip)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputVal.trim()) addChip(inputVal); }}
          placeholder={placeholder ?? "Escribí y presioná Enter"}
          className="min-h-[28px] flex-1 bg-transparent text-sm text-zinc-100 outline-none placeholder:text-zinc-600"
          aria-label={label}
        />
      </div>
    </div>
  );
}

interface MultiSelectCheckboxProps<T extends string> {
  legend: string;
  options: { value: T; label: string }[];
  value: T[];
  onChange(updated: T[]): void;
  prefix: string;
}

function MultiSelectCheckbox<T extends string>({
  legend,
  options,
  value,
  onChange,
  prefix,
}: MultiSelectCheckboxProps<T>): JSX.Element {
  const toggle = (v: T): void => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-zinc-300">{legend}</legend>
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-900/20"
          >
            <input
              id={`${prefix}-${opt.value}`}
              type="checkbox"
              checked={value.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              className="h-4 w-4 rounded border-zinc-600 accent-blue-500 focus-visible:ring-2 focus-visible:ring-blue-400"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Medical disclaimer
// ---------------------------------------------------------------------------

function MedicalDisclaimer(): JSX.Element {
  return (
    <div
      role="note"
      className="mb-4 rounded-lg border border-yellow-600/40 bg-yellow-900/20 px-4 py-3 text-xs text-yellow-200"
    >
      <strong>Aviso médico:</strong> AutoDieta no reemplaza una consulta médica. Ante condiciones
      clínicas, consultá con un profesional de la salud antes de realizar cambios en tu alimentación.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

interface SectionProps {
  legend: string;
  children: React.ReactNode;
}

function Section({ legend, children }: SectionProps): JSX.Element {
  return (
    <fieldset className="mb-6 rounded-xl border border-zinc-800 p-4">
      <legend className="px-2 text-sm font-bold text-zinc-200">{legend}</legend>
      <div className="mt-3 space-y-4">{children}</div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Input helper
// ---------------------------------------------------------------------------

interface TextInputProps {
  id: string;
  label: string;
  type?: string;
  value: string | number | undefined;
  onChange(val: string): void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  errorId?: string;
  hasError?: boolean;
  required?: boolean;
}

function TextInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  errorId,
  hasError,
  required,
}: TextInputProps): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-zinc-300">
        {label}
        {required ? <span className="ml-1 text-red-400" aria-hidden="true">*</span> : null}
      </label>
      <input
        id={id}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        aria-invalid={hasError ?? false}
        {...(errorId !== undefined ? { "aria-describedby": errorId } : {})}
        className={`min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm text-zinc-100 bg-zinc-900 placeholder:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          hasError ? "border-red-500" : "border-zinc-700"
        }`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekday activity constants
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS: string[] = [
  "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo",
];

const ACTIVITY_TYPE_OPTIONS: { value: DayActivityType; label: string }[] = [
  { value: "sleep", label: "Sueño" },
  { value: "work_sedentary", label: "Trabajo sedentario" },
  { value: "work_active", label: "Trabajo activo" },
  { value: "study", label: "Estudio" },
  { value: "training", label: "Entrenamiento" },
];

const INTENSITY_OPTIONS: { value: ActivityIntensity; label: string }[] = [
  { value: "light", label: "Ligero" },
  { value: "moderate", label: "Moderado" },
  { value: "hard", label: "Fuerte" },
  { value: "very_hard", label: "Muy fuerte" },
];

// ---------------------------------------------------------------------------
// WeekdayActivityRow — one row per activity entry within a weekday
// ---------------------------------------------------------------------------

/** Partial DayActivity for editing state — type and durationHours may be undefined mid-edit */
interface DayActivityDraft {
  type?: DayActivityType;
  durationHours?: number;
  intensity?: ActivityIntensity;
}

interface WeekdayActivityRowProps {
  weekdayIndex: number;
  activityIndex: number;
  activity: DayActivityDraft;
  onChange(updated: DayActivityDraft): void;
  onRemove(): void;
}

function WeekdayActivityRow({
  weekdayIndex,
  activityIndex,
  activity,
  onChange,
  onRemove,
}: WeekdayActivityRowProps): JSX.Element {
  const idPrefix = `wa-${weekdayIndex}-${activityIndex}`;
  const isTraining = activity.type === "training";

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-zinc-700 bg-zinc-800/50 p-2">
      {/* Activity type */}
      <div className="min-w-[140px] flex-1">
        <label htmlFor={`${idPrefix}-type`} className="mb-1 block text-xs font-medium text-zinc-400">
          Tipo de actividad
        </label>
        <select
          id={`${idPrefix}-type`}
          value={activity.type ?? ""}
          onChange={(e) => onChange({ ...activity, type: (e.target.value as DayActivityType) || undefined })}
          className="min-h-[44px] w-full rounded-md border border-zinc-600 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          <option value="">Seleccioná</option>
          {ACTIVITY_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Duration */}
      <div className="w-28">
        <label htmlFor={`${idPrefix}-duration`} className="mb-1 block text-xs font-medium text-zinc-400">
          Duración (horas)
        </label>
        <input
          id={`${idPrefix}-duration`}
          type="number"
          value={activity.durationHours ?? ""}
          onChange={(e) => {
            const parsed = e.target.value ? parseFloat(e.target.value) : undefined;
            const next: DayActivityDraft = { ...activity };
            if (parsed !== undefined) {
              next.durationHours = parsed;
            } else {
              delete next.durationHours;
            }
            onChange(next);
          }}
          min={0.25}
          max={24}
          step={0.25}
          aria-label="Duración en horas"
          className="min-h-[44px] w-full rounded-md border border-zinc-600 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        />
      </div>

      {/* Intensity (only for training) */}
      {isTraining ? (
        <div className="w-36">
          <label htmlFor={`${idPrefix}-intensity`} className="mb-1 block text-xs font-medium text-zinc-400">
            Intensidad / esfuerzo
          </label>
          <select
            id={`${idPrefix}-intensity`}
            value={activity.intensity ?? ""}
            onChange={(e) => onChange({ ...activity, intensity: (e.target.value as ActivityIntensity) || undefined })}
            className="min-h-[44px] w-full rounded-md border border-zinc-600 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            <option value="">Seleccioná</option>
            {INTENSITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Remove button */}
      <button
        type="button"
        aria-label="Eliminar actividad"
        onClick={onRemove}
        className="min-h-[44px] rounded-md border border-zinc-600 px-3 py-1 text-sm text-zinc-400 hover:border-red-500 hover:text-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        ×
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WeekdayActivitySection — one collapsible fieldset per weekday
// ---------------------------------------------------------------------------

/** Convert DayActivityDraft to DayActivity — only well-formed drafts pass */
function draftToActivity(draft: DayActivityDraft): DayActivity | undefined {
  if (!draft.type || draft.durationHours === undefined || draft.durationHours <= 0) {
    return undefined;
  }
  if (draft.type === "training" && !draft.intensity) {
    return undefined;
  }
  const base = { type: draft.type, durationHours: draft.durationHours };
  if (draft.type === "training" && draft.intensity) {
    return { ...base, intensity: draft.intensity };
  }
  return base;
}

interface WeekdayActivitySectionProps {
  weekdayIndex: number;
  entry: WeekdayActivity | undefined;
  onChange(updated: WeekdayActivity): void;
}

function WeekdayActivitySection({
  weekdayIndex,
  entry,
  onChange,
}: WeekdayActivitySectionProps): JSX.Element {
  const label = WEEKDAY_LABELS[weekdayIndex] ?? `Día ${weekdayIndex}`;
  const activities: DayActivityDraft[] = entry?.activities ?? [];

  const dispatchChange = useCallback(
    (drafts: DayActivityDraft[]): void => {
      const valid = drafts.flatMap((d) => {
        const a = draftToActivity(d);
        return a ? [a] : [];
      });
      onChange({ weekday: weekdayIndex as WeekdayActivity["weekday"], activities: valid });
    },
    [weekdayIndex, onChange],
  );

  const addActivity = useCallback((): void => {
    const next = [...activities, { type: "work_sedentary" as const, durationHours: 8 }];
    dispatchChange(next);
  }, [activities, dispatchChange]);

  const updateActivity = useCallback(
    (index: number, updated: DayActivityDraft): void => {
      const next = activities.map((a, i) => (i === index ? updated : a));
      dispatchChange(next);
    },
    [activities, dispatchChange],
  );

  const removeActivity = useCallback(
    (index: number): void => {
      const next = activities.filter((_, i) => i !== index);
      dispatchChange(next);
    },
    [activities, dispatchChange],
  );

  return (
    <fieldset className="rounded-lg border border-zinc-700 p-3">
      <legend className="px-2 text-sm font-semibold text-zinc-200">{label}</legend>
      <div className="mt-2 space-y-2">
        {activities.map((act, i) => (
          <WeekdayActivityRow
            key={i}
            weekdayIndex={weekdayIndex}
            activityIndex={i}
            activity={act}
            onChange={(updated) => updateActivity(i, updated)}
            onRemove={() => removeActivity(i)}
          />
        ))}
        <button
          type="button"
          onClick={addActivity}
          className="min-h-[44px] rounded-md border border-zinc-600 px-3 py-1 text-sm text-zinc-400 hover:border-blue-500 hover:text-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          + Agregar actividad
        </button>
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// ProfileForm
// ---------------------------------------------------------------------------

export function ProfileForm({
  value,
  onChange,
  onSubmit,
  saving,
  error,
  successMessage,
}: ProfileFormProps): JSX.Element {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const firstErrorRef = useRef<HTMLElement | null>(null);

  // Helper to update a single field
  const set = useCallback(
    <K extends keyof HealthProfile>(field: K, val: HealthProfile[K] | undefined) => {
      const update: Partial<HealthProfile> = {};
      if (val !== undefined) {
        (update as Record<string, unknown>)[field] = val;
      }
      onChange({ ...value, ...update });
    },
    [value, onChange],
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Client-side range validation
      const rangeResult = validateHealthProfileRanges(value);
      const enumResult = validateEnums(value);

      const newErrors: Record<string, string> = {};
      for (const err of rangeResult.errors) {
        newErrors[err.field] = `${err.field}: ${err.reason}`;
      }
      for (const err of enumResult.errors) {
        newErrors[err.field] = `${err.field}: ${err.reason}`;
      }

      setFieldErrors(newErrors);

      if (!rangeResult.valid || !enumResult.valid) {
        // Focus the first invalid field
        const form = e.currentTarget;
        const first = form.querySelector<HTMLElement>("[aria-invalid='true']");
        first?.focus();
        firstErrorRef.current = first;
        return;
      }

      onSubmit(value);
    },
    [value, onSubmit],
  );

  const allergies = value.allergies ?? [];
  const intolerances = value.intolerances ?? [];
  const conditions = value.conditions ?? [];
  const preferences = value.preferences ?? [];
  const aversions = value.aversions ?? [];
  const medications = value.medications ?? [];
  const supplements = value.supplements ?? [];
  const weeklyActivity = value.weeklyActivity ?? [];

  const updateWeekdayActivity = useCallback(
    (weekdayIndex: number, updated: WeekdayActivity): void => {
      const existing = weeklyActivity.filter((e) => e.weekday !== weekdayIndex);
      const next = [...existing, updated].sort((a, b) => a.weekday - b.weekday);
      set("weeklyActivity", next.length > 0 ? next : undefined);
    },
    [weeklyActivity, set],
  );

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Formulario de perfil de salud">
      {/* Global error */}
      {error !== undefined ? (
        <div role="alert" className="mb-4 rounded-lg border border-red-600/40 bg-red-900/20 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      {/* Success message */}
      {successMessage !== undefined ? (
        <div role="status" aria-live="polite" className="mb-4 rounded-lg border border-green-600/40 bg-green-900/20 px-4 py-3 text-sm text-green-300">
          {successMessage}
        </div>
      ) : null}

      {/* Section 1 — Personal data */}
      <Section legend="Datos personales">
        <TextInput
          id="displayName"
          label="Nombre para mostrar"
          value={value.displayName}
          onChange={(v) => set("displayName", v || undefined)}
          placeholder="Cómo querés que te llamemos"
        />

        <div>
          <label htmlFor="sex" className="mb-1 block text-sm font-medium text-zinc-300">
            Sexo biológico <span className="ml-1 text-red-400" aria-hidden="true">*</span>
          </label>
          <select
            id="sex"
            value={value.sex ?? ""}
            onChange={(e) =>
              set("sex", (e.target.value as "male" | "female") || undefined)
            }
            aria-invalid={!!fieldErrors["sex"]}
            {...(fieldErrors["sex"] !== undefined ? { "aria-describedby": "sex-error" } : {})}
            className={`min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm bg-zinc-900 text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
              fieldErrors["sex"] ? "border-red-500" : "border-zinc-700"
            }`}
          >
            <option value="">Seleccioná</option>
            <option value="female">Femenino</option>
            <option value="male">Masculino</option>
          </select>
          {fieldErrors["sex"] !== undefined ? (
            <FieldError id="sex-error" message={fieldErrors["sex"]} />
          ) : null}
        </div>

        <TextInput
          id="birthDate"
          label="Fecha de nacimiento"
          type="date"
          value={value.birthDate}
          onChange={(v) => set("birthDate", v || undefined)}
        />
      </Section>

      {/* Section 2 — Anthropometric */}
      <Section legend="Datos antropométricos">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <TextInput
              id="weightKg"
              label="Peso actual (kg)"
              type="number"
              value={value.weightKg}
              onChange={(v) => set("weightKg", v ? parseFloat(v) : undefined)}
              min={20}
              max={400}
              step={0.1}
              errorId="weightKg-error"
              hasError={!!fieldErrors["weightKg"]}
              required
            />
            {fieldErrors["weightKg"] !== undefined ? (
              <FieldError id="weightKg-error" message={fieldErrors["weightKg"]} />
            ) : null}
          </div>

          <div>
            <TextInput
              id="heightCm"
              label="Altura (cm)"
              type="number"
              value={value.heightCm}
              onChange={(v) => set("heightCm", v ? parseFloat(v) : undefined)}
              min={80}
              max={250}
              step={0.5}
              errorId="heightCm-error"
              hasError={!!fieldErrors["heightCm"]}
              required
            />
            {fieldErrors["heightCm"] !== undefined ? (
              <FieldError id="heightCm-error" message={fieldErrors["heightCm"]} />
            ) : null}
          </div>

          <div>
            <TextInput
              id="targetWeightKg"
              label="Peso objetivo (kg)"
              type="number"
              value={value.targetWeightKg}
              onChange={(v) => set("targetWeightKg", v ? parseFloat(v) : undefined)}
              min={20}
              max={400}
              step={0.1}
              errorId="targetWeightKg-error"
              hasError={!!fieldErrors["targetWeightKg"]}
            />
            {fieldErrors["targetWeightKg"] !== undefined ? (
              <FieldError id="targetWeightKg-error" message={fieldErrors["targetWeightKg"]} />
            ) : null}
          </div>

          <div>
            <TextInput
              id="bodyFatPct"
              label="% grasa corporal (opcional)"
              type="number"
              value={value.bodyFatPct}
              onChange={(v) => set("bodyFatPct", v ? parseFloat(v) : undefined)}
              min={0}
              max={60}
              step={0.5}
              errorId="bodyFatPct-error"
              hasError={!!fieldErrors["bodyFatPct"]}
            />
            {fieldErrors["bodyFatPct"] !== undefined ? (
              <FieldError id="bodyFatPct-error" message={fieldErrors["bodyFatPct"]} />
            ) : null}
          </div>

          <div>
            <TextInput
              id="waistCm"
              label="Cintura (cm, opcional)"
              type="number"
              value={value.waistCm}
              onChange={(v) => set("waistCm", v ? parseFloat(v) : undefined)}
              min={30}
              max={250}
              step={0.5}
              errorId="waistCm-error"
              hasError={!!fieldErrors["waistCm"]}
            />
            {fieldErrors["waistCm"] !== undefined ? (
              <FieldError id="waistCm-error" message={fieldErrors["waistCm"]} />
            ) : null}
          </div>

          <div>
            <TextInput
              id="hipCm"
              label="Cadera (cm, opcional)"
              type="number"
              value={value.hipCm}
              onChange={(v) => set("hipCm", v ? parseFloat(v) : undefined)}
              min={30}
              max={250}
              step={0.5}
              errorId="hipCm-error"
              hasError={!!fieldErrors["hipCm"]}
            />
            {fieldErrors["hipCm"] !== undefined ? (
              <FieldError id="hipCm-error" message={fieldErrors["hipCm"]} />
            ) : null}
          </div>
        </div>
      </Section>

      {/* Section 3 — Objective & Activity */}
      <Section legend="Objetivo y actividad física">
        <div>
          <label htmlFor="activityLevel" className="mb-1 block text-sm font-medium text-zinc-300">
            Nivel de actividad <span className="ml-1 text-red-400" aria-hidden="true">*</span>
          </label>
          <select
            id="activityLevel"
            value={value.activityLevel ?? ""}
            onChange={(e) =>
              set("activityLevel", (e.target.value as ActivityLevel) || undefined)
            }
            className="min-h-[44px] w-full rounded-lg border border-zinc-700 px-3 py-2 text-sm bg-zinc-900 text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            <option value="">Seleccioná tu nivel de actividad</option>
            {ACTIVITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="objective" className="mb-1 block text-sm font-medium text-zinc-300">
            Objetivo calórico <span className="ml-1 text-red-400" aria-hidden="true">*</span>
          </label>
          <select
            id="objective"
            value={value.objective ?? ""}
            onChange={(e) =>
              set("objective", (e.target.value as Objective) || undefined)
            }
            className="min-h-[44px] w-full rounded-lg border border-zinc-700 px-3 py-2 text-sm bg-zinc-900 text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            <option value="">Seleccioná tu objetivo</option>
            {OBJECTIVE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <TextInput
          id="ageYears"
          label="Edad (años)"
          type="number"
          value={value.ageYears}
          onChange={(v) => set("ageYears", v ? parseInt(v, 10) : undefined)}
          min={5}
          max={120}
          step={1}
          errorId="ageYears-error"
          hasError={!!fieldErrors["ageYears"]}
          required
        />
        {fieldErrors["ageYears"] !== undefined ? (
          <FieldError id="ageYears-error" message={fieldErrors["ageYears"]} />
        ) : null}
      </Section>

      {/* Section 4 — Dietary pattern */}
      <Section legend="Patrón alimentario">
        <div>
          <label htmlFor="dietType" className="mb-1 block text-sm font-medium text-zinc-300">
            Tipo de dieta <span className="ml-1 text-red-400" aria-hidden="true">*</span>
          </label>
          <select
            id="dietType"
            value={value.dietType ?? ""}
            onChange={(e) =>
              set("dietType", (e.target.value as DietType) || undefined)
            }
            className="min-h-[44px] w-full rounded-lg border border-zinc-700 px-3 py-2 text-sm bg-zinc-900 text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            <option value="">Seleccioná tu patrón alimentario</option>
            {DIET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <TextInput
          id="mealsPerDay"
          label="Comidas por día"
          type="number"
          value={value.mealsPerDay}
          onChange={(v) => set("mealsPerDay", v ? parseInt(v, 10) : undefined)}
          min={1}
          max={10}
          step={1}
          errorId="mealsPerDay-error"
          hasError={!!fieldErrors["mealsPerDay"]}
        />
        {fieldErrors["mealsPerDay"] !== undefined ? (
          <FieldError id="mealsPerDay-error" message={fieldErrors["mealsPerDay"]} />
        ) : null}

        <ChipsInput
          id="preferences"
          label="Alimentos que preferís (chips)"
          value={preferences}
          onChange={(v) => set("preferences", v.length > 0 ? v : undefined)}
          placeholder="Escribí y presioná Enter o coma"
        />

        <ChipsInput
          id="aversions"
          label="Alimentos que no te gustan (chips)"
          value={aversions}
          onChange={(v) => set("aversions", v.length > 0 ? v : undefined)}
          placeholder="Escribí y presioná Enter o coma"
        />

        <TextInput
          id="religiousEthical"
          label="Restricciones religiosas / éticas (opcional)"
          value={value.religiousEthical?.join(", ")}
          onChange={(v) =>
            set(
              "religiousEthical",
              v
                ? v.split(",").map((s) => s.trim()).filter(Boolean)
                : undefined,
            )
          }
          placeholder="Ej: sin cerdo, halal, kosher"
        />
      </Section>

      {/* Medical disclaimer above clinical sections */}
      <MedicalDisclaimer />

      {/* Section 5 — Allergies */}
      <Section legend="Alergias alimentarias">
        <MultiSelectCheckbox
          legend="Seleccioná tus alergias (si aplica)"
          options={ALLERGEN_OPTIONS}
          value={allergies}
          onChange={(v) => set("allergies", v.length > 0 ? v : [])}
          prefix="allergen"
        />
        <p className="text-xs text-zinc-500">
          Si no tenés alergias, dejá todo sin marcar.
        </p>
      </Section>

      {/* Section 6 — Intolerances */}
      <Section legend="Intolerancias alimentarias">
        <MultiSelectCheckbox
          legend="Seleccioná tus intolerancias (si aplica)"
          options={INTOLERANCE_OPTIONS}
          value={intolerances}
          onChange={(v) => set("intolerances", v.length > 0 ? v : [])}
          prefix="intolerance"
        />
      </Section>

      {/* Section 7 — Medical conditions */}
      <Section legend="Condiciones médicas">
        <MultiSelectCheckbox
          legend="Seleccioná tus condiciones médicas (si aplica)"
          options={CONDITION_OPTIONS}
          value={conditions}
          onChange={(v) => set("conditions", v.length > 0 ? v : [])}
          prefix="condition"
        />

        <div>
          <label htmlFor="conditionsOther" className="mb-1 block text-sm font-medium text-zinc-300">
            Otras condiciones (texto libre)
          </label>
          <textarea
            id="conditionsOther"
            value={value.conditionsOther ?? ""}
            onChange={(e) => set("conditionsOther", e.target.value || undefined)}
            rows={2}
            placeholder="Describí otras condiciones no listadas arriba"
            className="min-h-[44px] w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          />
        </div>
      </Section>

      {/* Section 8 — Medications & supplements */}
      <Section legend="Medicamentos y suplementos">
        <ChipsInput
          id="medications"
          label="Medicamentos actuales"
          value={medications}
          onChange={(v) => set("medications", v.length > 0 ? v : undefined)}
          placeholder="Escribí el nombre y presioná Enter"
        />
        <ChipsInput
          id="supplements"
          label="Suplementos actuales"
          value={supplements}
          onChange={(v) => set("supplements", v.length > 0 ? v : undefined)}
          placeholder="Proteína, creatina, etc."
        />
      </Section>

      {/* Section 9 — Notes / lifestyle */}
      <Section legend="Notas y estilo de vida">
        <div>
          <label htmlFor="notes" className="mb-1 block text-sm font-medium text-zinc-300">
            Notas adicionales
          </label>
          <textarea
            id="notes"
            value={value.notes ?? ""}
            onChange={(e) => set("notes", e.target.value || undefined)}
            rows={4}
            placeholder="Digestión, estilo de vida, horarios de comida, otras notas relevantes..."
            className="min-h-[44px] w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          />
        </div>
      </Section>

      {/* Section 10 — Actividad semanal (F7, optional — no onboarding gate) */}
      {/*
       * DRAFT — pending nutritionist sign-off on meal-window default times
       * and training schedule display copy.
       */}
      <Section legend="Actividad semanal">
        <p className="text-xs text-zinc-500">
          Opcional. Completá tu rutina diaria para obtener objetivos nutricionales personalizados
          por día de la semana. Si no la completás, se usará tu nivel de actividad general.
        </p>
        <div className="space-y-3">
          {([0, 1, 2, 3, 4, 5, 6] as const).map((weekdayIndex) => (
            <WeekdayActivitySection
              key={weekdayIndex}
              weekdayIndex={weekdayIndex}
              entry={weeklyActivity.find((e) => e.weekday === weekdayIndex)}
              onChange={(updated) => updateWeekdayActivity(weekdayIndex, updated)}
            />
          ))}
        </div>
      </Section>

      {/* Submit */}
      <div className="sticky bottom-0 bg-zinc-950/90 pt-2 pb-4 backdrop-blur">
        <button
          type="submit"
          disabled={saving}
          aria-busy={saving}
          className="min-h-[44px] w-full rounded-xl bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          {saving ? "Guardando..." : "Guardar perfil"}
        </button>
      </div>
    </form>
  );
}
